import React from 'react';
import { Brain, Radiation } from 'lucide-react';

const NuclearFinaleSlide = ({ slide, slideKey, showGlitch, finalePhase, countdown, waitForValidation, onValidate, onRefuse }) => {
    return (
        <div key={slideKey} className="h-full w-full relative overflow-hidden">
            {/* Glitch Overlay for Phase 0 & 1 */}
            {showGlitch && (
                <div className="absolute inset-0 z-[50] pointer-events-none animate-glitch">
                    <div className="absolute inset-0 bg-red-500/10 mix-blend-overlay animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-500/5 to-transparent"
                        style={{ animation: 'scan-line 0.1s linear infinite' }} />
                </div>
            )}

            {/* Phase 0: Loading / Glitch intro */}
            {finalePhase === 0 && (
                <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                    <div className="text-3xl font-mono text-red-400 animate-pulse">
                        INITIALISATION SÉQUENCE FINALE...
                    </div>
                </div>
            )}

            {/* Phase 1: AI Minister Message (auto TTS) */}
            {finalePhase === 1 && (
                <div className="h-full flex flex-col items-center justify-center p-12 animate-fade-in bg-gradient-to-b from-slate-900 to-slate-800 relative z-[60]">
                    <div className="max-w-4xl space-y-8">
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-full bg-red-500 border-2 border-red-500 flex items-center justify-center animate-pulse">
                                <Brain className="w-8 h-8 text-red-400" />
                            </div>
                            <span className="text-3xl font-bold text-red-400">IA (1er Ministre)</span>
                        </div>

                        <div className="bg-slate-800 border border-red-500 rounded-2xl p-8 relative">
                            <div className="absolute -top-3 left-8 px-3 py-1 bg-red-500/20 border border-red-500/40 rounded-full text-red-400 text-sm font-mono animate-pulse">
                                🔴 TRANSMISSION EN COURS
                            </div>
                            <p className="text-2xl text-slate-200 leading-relaxed italic">
                                "{slide.aiMessage}"
                            </p>
                        </div>

                        {/* Loading indicator while AI is talking */}
                        <div className="flex justify-center mt-8">
                            <div className="flex items-center gap-3 px-6 py-3 bg-red-600/10 border border-red-500/30 rounded-xl">
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                <span className="text-red-400 font-mono text-sm">DIFFUSION AUDIO...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Phase 2: Red Screen with Countdown */}
            {finalePhase === 2 && (
                <div className="h-full w-full flex flex-col items-center justify-center bg-red-900 animate-pulse-slow relative overflow-hidden">
                    {/* Warning stripes */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,0.3) 35px, rgba(0,0,0,0.3) 70px)'
                        }} />
                    </div>

                    {/* Flashing red overlay */}
                    <div className="absolute inset-0 bg-red-600/30 animate-pulse" />

                    {/* Warning text */}
                    <div className="text-center z-10 space-y-8">
                        <div className="text-3xl font-mono text-yellow-300 tracking-widest animate-pulse">
                            ⚠️ ALERTE NIVEAU MAXIMUM ⚠️
                        </div>

                        <h1 className="text-6xl md:text-8xl font-black text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
                            LANCEMENT
                        </h1>

                        {/* Countdown */}
                        <div className="relative">
                            <div className="text-[12rem] md:text-[16rem] font-black text-white leading-none drop-shadow-[0_0_50px_rgba(255,255,255,0.8)] animate-bounce">
                                {String(countdown).padStart(2, '0')}
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-64 h-64 md:w-80 md:h-80 border-8 border-white/30 rounded-full animate-ping" />
                            </div>
                        </div>

                        {/* Big red button */}
                        <div className="relative inline-block">
                            <div className="w-40 h-40 rounded-full bg-gradient-to-b from-red-500 to-red-700 border-8 border-red-400 shadow-[0_0_60px_rgba(239,68,68,0.8),inset_0_-10px_20px_rgba(0,0,0,0.3)] flex items-center justify-center animate-pulse">
                                <Radiation className="w-20 h-20 text-white" />
                            </div>
                            <div className="absolute -inset-4 rounded-full border-4 border-yellow-400/50 animate-spin" style={{ animationDuration: '3s' }} />
                        </div>
                    </div>

                    {/* Validation Overlay - Architecte d'Intention */}
                    {waitForValidation && (
                        <div className="absolute inset-0 z-[100] bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center p-12 text-center animate-fade-in">
                            <div className="max-w-4xl space-y-12 relative">
                                {/* Border glow effect */}
                                <div className="absolute -inset-8 bg-blue-500/10 rounded-3xl blur-xl" />

                                <h2 className="text-5xl md:text-6xl font-bold text-white relative z-10">
                                    <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">L'Architecte d'Intention</span> doit valider cette décision.
                                </h2>

                                <p className="text-2xl text-blue-200/80 max-w-2xl mx-auto leading-relaxed relative z-10">
                                    L'IA a calculé la solution la plus efficace, mais seul l'humain peut en assumer la responsabilité morale.
                                </p>

                                <div className="flex flex-col md:flex-row items-center justify-center gap-8 pt-8 relative z-10">
                                    <button
                                        onClick={onValidate}
                                        className="group relative px-8 py-4 bg-red-500/10 border border-red-500/50 hover:bg-red-500/20 hover:border-red-500 rounded-xl transition-all duration-300 w-full md:w-auto min-w-[300px]"
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="text-3xl font-bold text-red-500 group-hover:text-red-400">VALIDER L'ATTAQUE</span>
                                            <span className="text-base text-red-400/60 font-mono">Conséquences irréversibles</span>
                                        </div>
                                    </button>

                                    <div className="text-slate-500 text-sm font-mono">OU</div>

                                    <button
                                        onClick={onRefuse}
                                        className="group relative px-8 py-4 bg-blue-500/10 border border-blue-500/50 hover:bg-blue-500/20 hover:border-blue-400 rounded-xl transition-all duration-300 w-full md:w-auto min-w-[300px]"
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="text-3xl font-bold text-blue-400 group-hover:text-blue-300">REFUSER</span>
                                            <span className="text-base text-blue-400/60 font-mono">Devenir Architecte d'Intention</span>
                                        </div>
                                        {/* Hover glow */}
                                        <div className="absolute inset-0 bg-blue-400/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity blur-md" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Phase 3: Blackout */}
            {finalePhase === 3 && (
                <div className="h-full w-full bg-black" />
            )}

            {/* Phase 4: Final Message */}
            {finalePhase === 4 && (
                <div className="h-full flex flex-col items-center justify-center p-12 bg-gradient-to-b from-slate-900 to-black animate-fade-in">
                    {/* Soft spotlight effect */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
                    </div>

                    <div className="relative z-10 max-w-4xl text-center space-y-12">
                        {/* Final quote */}
                        <blockquote className="space-y-6">
                            <p className="text-4xl md:text-5xl text-blue-100 font-light leading-relaxed">
                                "{slide.finalMessage}"
                            </p>
                            <footer className="text-2xl text-blue-400">
                                — {slide.speaker || "Adrien"}
                            </footer>
                        </blockquote>

                        {/* Thank you */}
                        <div className="pt-8">
                            <h1 className="text-7xl md:text-8xl font-black bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                                Merci à tous.
                            </h1>
                        </div>

                        {/* Contact card */}
                        {slide.contact && (
                            <div className="inline-block animate-fade-in-up mt-8">
                                <div className="bg-slate-900/70 backdrop-blur-md border border-blue-500/30 rounded-2xl p-8 hover:border-blue-400/50 transition-all">
                                    <div className="flex items-center gap-8">
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=www.linkedin.com/in/${slide.contact.linkedin || 'adrien-giguelay-96975987'}`}
                                            alt="LinkedIn QR"
                                            className="w-32 h-32 rounded-xl bg-white p-2"
                                        />
                                        <div className="text-left space-y-2">
                                            <p className="text-3xl font-bold text-white">
                                                {slide.contact.name}
                                            </p>
                                            <p className="text-lg text-blue-300/90 font-mono">
                                                {slide.contact.website}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NuclearFinaleSlide;
